import { useState } from "react";
import { Package, Images, Check } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { PhotoLightbox } from "./PhotoLightbox";

const BASE = `${process.env.REACT_APP_BACKEND_URL}/api/documents/`;

export const MonteurKomponenten = ({ project, phaseKey }) => {
  const [mats, setMats] = useState(project.materials || []);
  const done = mats.filter((m) => m.verbaut).length;

  const toggle = async (i) => {
    const next = !mats[i].verbaut;
    setMats((ms) => ms.map((m, j) => (j === i ? { ...m, verbaut: next } : m)));
    try {
      const { data } = await api.patch(`/projects/${project.id}/materials/${i}/verbaut`, { verbaut: next });
      setMats((ms) => ms.map((m, j) => (j === i ? data : m)));
    } catch {
      setMats((ms) => ms.map((m, j) => (j === i ? { ...m, verbaut: !next } : m)));
      toast.error("Speichern fehlgeschlagen");
    }
  };

  return (
    <div className="space-y-1.5" data-testid={`assignment-komponenten-${phaseKey}`}>
      <div className="text-[11px] uppercase tracking-wider text-muted-foreground flex items-center gap-1">
        <Package className="h-3.5 w-3.5" /> Komponenten
        {mats.length > 0 && <span className="ml-auto normal-case tracking-normal" data-testid={`komponenten-progress-${phaseKey}`}>{done} / {mats.length} verbaut</span>}
      </div>
      {mats.length === 0 ? (
        <div className="rounded-md border border-border/60 bg-muted/40 px-3 py-2 text-xs italic text-muted-foreground">Keine Komponenten hinterlegt</div>
      ) : (
        <ul className="divide-y divide-border/60 rounded-md border border-border/60 bg-background">
          {mats.map((m, i) => (
            <li key={i}>
              <button type="button" onClick={() => toggle(i)}
                className={`w-full flex items-center gap-3 px-3 py-2.5 text-sm text-left transition-colors ${m.verbaut ? "bg-primary/5" : "hover:bg-muted/50"}`}
                data-testid={`komponente-toggle-${phaseKey}-${i}`}>
                <span className={`h-6 w-6 rounded-md border-2 flex items-center justify-center shrink-0 transition-colors ${m.verbaut ? "bg-primary border-primary text-primary-foreground" : "border-border"}`}>
                  {m.verbaut && <Check className="h-4 w-4" />}
                </span>
                <span className="font-mono text-xs text-muted-foreground shrink-0">{m.menge}×</span>
                <span className={`flex-1 min-w-0 break-words ${m.verbaut ? "line-through text-muted-foreground" : ""}`}>{m.artikel}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export const MonteurProjektbilder = ({ project, phaseKey }) => {
  const [open, setOpen] = useState(null);
  const groups = (project.phases || []).filter((p) => p.key !== phaseKey && (p.photos || []).length)
    .map((p) => ({ key: p.key, label: p.label, photos: p.photos.filter((f) => !f.path?.endsWith?.(".pdf")) }));
  const imgs = (project.documents || []).filter((d) => /\.(jpe?g|png|webp|heic|gif)$/i.test(d.path || d.name || ""));
  if (imgs.length) groups.push({ key: "_files", label: "Dateiablage", photos: imgs });
  const flat = groups.flatMap((g) => g.photos.map((p) => ({ src: BASE + p.path, group: g.label })));
  let k = 0;
  return (
    <div className="space-y-2" data-testid={`assignment-projektbilder-${phaseKey}`}>
      <div className="text-[11px] uppercase tracking-wider text-muted-foreground flex items-center gap-1">
        <Images className="h-3.5 w-3.5" /> Projektbilder
      </div>
      {flat.length === 0 && (
        <div className="rounded-md border border-border/60 bg-muted/40 px-3 py-2 text-xs italic text-muted-foreground">Keine weiteren Projektbilder</div>
      )}
      {groups.filter((g) => g.photos.length).map((g) => (
        <div key={g.key} className="space-y-1">
          <div className="text-xs font-medium">{g.label} ({g.photos.length})</div>
          <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
            {g.photos.map((p) => {
              const idx = k++;
              return (
                <button type="button" key={p.id || p.path} onClick={() => setOpen(idx)}
                  className="aspect-square rounded-md overflow-hidden bg-muted border border-border"
                  data-testid={`projektbild-${idx}`}>
                  <img src={BASE + p.path} alt={p.name || ""} loading="lazy" className="w-full h-full object-cover hover:scale-105 transition-transform" />
                </button>
              );
            })}
          </div>
        </div>
      ))}
      {open !== null && <PhotoLightbox photos={flat} index={open} onIndex={setOpen} onClose={() => setOpen(null)} />}
    </div>
  );
};
